/* eslint-disable max-statements */
import { ActionType, AppError } from './types';
import * as api from './api';
import * as ethereum from '../utils/ethereum';
import { EipError } from '@wolffun/shared/src/types';
import { WalletStatusResponse } from '../utils/types';
import { WalletProvider } from '@wolffun/shared/src/types';
import { CurrencyType, systemCurrencyToBlockchainNumber } from '@wolffun/system-currency';
import { SYSTEM_CURRENCY_DECIMALS } from '@wolffun/shared/constants';
import omitBy from 'lodash/omitBy';
import isUndefined from 'lodash/isUndefined';
import { clearPreferredWalletProviderName, clearSession, getPreferredWalletProviderName, getSession, storeSession } from '@wolffun/shared/src/utils/session';
import { destroyWalletClient } from '@wolffun/shared/src/utils/walletClient';
import { personalSignPolygon } from '@wolffun/shared/src/utils/ethereum.util';
import { parseServerCurrency } from './utils';
import noop from 'lodash/noop';

const invalidateConfirmCashout = () => ({
  type: ActionType.INVALIDATE_CONFIRM_CASHOUT,
  payload: undefined
});

const confirmCashoutRequested = () => ({
  type: ActionType.CONFIRM_CASHOUT_REQUESTED,
  payload: undefined
});

const confirmCashoutSuccess = data => ({
  type: ActionType.CONFIRM_CASHOUT_SUCCESS,
  payload: data
});

const confirmCashoutFailure = error => ({
  type: ActionType.CONFIRM_CASHOUT_FAILURE,
  payload: {
    error
  }
});

export const confirmCashout = price => async dispatch => {
  dispatch(invalidateConfirmCashout());
  dispatch(confirmCashoutRequested());
  const response = await api.postConfirmCashout({
    type: price.token,
    value: Number(price.value),
    decimals: SYSTEM_CURRENCY_DECIMALS
  });

  if (response.status === 'error') {
    dispatch(confirmCashoutFailure(response.error));
    return {
      error: response.error
    };
  }

  dispatch(confirmCashoutSuccess(response.body));
  return {
    data: response.body,
    error: AppError.NO_ERROR
  };
};

const invalidateCashOutLimit = () => ({
  type: ActionType.INVALIDATE_CASHOUT_LIMIT,
  payload: undefined
});

const fetchCashOutLimitRequested = () => ({
  type: ActionType.FETCH_CASHOUT_LIMIT_REQUESTED,
  payload: undefined
});

const fetchCashOutLimitSuccess = data => ({
  type: ActionType.FETCH_CASHOUT_LIMIT_SUCCESS,
  payload: {
    data
  }
});

const fetchCashOutLimitFailure = error => ({
  type: ActionType.FETCH_CASHOUT_LIMIT_FAILURE,
  payload: {
    error
  }
});

export const fetchCashOutLimit = type => async dispatch => {
  dispatch(invalidateCashOutLimit());
  dispatch(fetchCashOutLimitRequested());
  const response = await api.getLimitCashout({
    currency: type.toLowerCase()
  });

  if (response.status === 'error') {
    dispatch(fetchCashOutLimitFailure(response.error));
    return {
      error: response.error
    };
  }

  dispatch(fetchCashOutLimitSuccess(response.body));
  return {
    data: response.body
  };
};

const fetchCheckAccountCashoutRequested = () => ({
  type: ActionType.FETCH_CHECK_ACCOUNT_CASHOUT_REQUESTED,
  payload: undefined
});

const fetchCheckAccountSuccess = () => ({
  type: ActionType.FETCH_CHECK_ACCOUNT_CASHOUT_SUCCESS,
  payload: undefined
});

const fetchCheckAccountFailure = error => ({
  type: ActionType.FETCH_CHECK_ACCOUNT_CASHOUT_FAILURE,
  payload: {
    error
  }
});

export const checkAccountCashout = currencyType => async dispatch => {
  dispatch(fetchCheckAccountCashoutRequested());
  const response = await api.getCheckAccountCashout({
    type: currencyType
  });

  if (response.status === 'error') {
    dispatch(fetchCheckAccountFailure(response.error));
    return {
      error: response.error
    };
  }

  dispatch(fetchCheckAccountSuccess());
  return {};
};

const updateAvatarRequested = () => ({
  type: ActionType.UPDATE_AVATAR_REQUESTED,
  payload: undefined
});

const updateAvatarSuccess = () => ({
  type: ActionType.UPDATE_AVATAR_SUCCESS,
  payload: undefined
});

const updateAvatarFailure = error => ({
  type: ActionType.UPDATE_AVATAR_FAILURE,
  payload: {
    error
  }
});

export const updateAvatar = (type, selected) => async dispatch => {
  dispatch(updateAvatarRequested());
  const response = await api.putSelectCosmetics({
    type,
    selected
  });

  if (response.status === 'error') {
    dispatch(updateAvatarFailure(response.error));
    return {
      error: response.error
    };
  }

  const profileResponse = await dispatch(updateUserProfile());

  if (profileResponse.error) {
    dispatch(updateAvatarFailure(profileResponse.error));
    return {
      error: profileResponse.error
    };
  }

  dispatch(updateAvatarSuccess());
  return {};
};

const fetchAvatarConfigRequested = () => ({
  type: ActionType.FETCH_AVATAR_CONFIG_REQUESTED,
  payload: undefined
});

const fetchAvatarConfigSuccess = (avatarConfigVer, avatarConfigs) => ({
  type: ActionType.FETCH_AVATAR_CONFIG_SUCCESS,
  payload: {
    avatarConfigVer,
    data: avatarConfigs
  }
});

const fetchAvatarConfigFailure = error => ({
  type: ActionType.FETCH_AVATAR_CONFIG_FAILURE,
  payload: {
    error
  }
});

export const getAvatarConfigs = () => async (dispatch, getState) => {
  dispatch(fetchAvatarConfigRequested());
  const avatarResponse = await api.getAvatarConfigs({
    configVer: getState().avatarConfigs.avatarConfigVer
  });

  if (avatarResponse.status === 'error') {
    dispatch(fetchAvatarConfigFailure(avatarResponse.error));
    return {
      error: avatarResponse.error
    };
  }

  const {
    avatarConfigVer,
    avatarConfigs
  } = avatarResponse.body;
  if (!!avatarConfigVer && !!avatarConfigs) dispatch(fetchAvatarConfigSuccess(avatarConfigVer, avatarConfigs));
  return {
    data: avatarConfigs
  };
};

const fetchAvatarAllowedConfigRequested = () => ({
  type: ActionType.FETCH_AVATAR_ALLOWED_CONFIG_REQUESTED,
  payload: undefined
});

const fetchAvatarAllowedConfigSuccess = avatarAllowedConfigs => ({
  type: ActionType.FETCH_AVATAR_ALLOWED_CONFIG_SUCCESS,
  payload: {
    data: avatarAllowedConfigs
  }
});

const fetchAvatarAllowedConfigFailure = error => ({
  type: ActionType.FETCH_AVATAR_ALLOWED_CONFIG_FAILURE,
  payload: {
    error
  }
});

const exchangeCurrencyRequested = () => ({
  type: ActionType.EXCHANGE_CURRENCY_REQUESTED,
  payload: undefined
});

const exchangeCurrencySuccess = () => ({
  type: ActionType.EXCHANGE_CURRENCY_SUCCESS,
  payload: undefined
});

const exchangeCurrencyFailure = error => ({
  type: ActionType.EXCHANGE_CURRENCY_FAILURE,
  payload: {
    error
  }
});

export const exchangeCurrency = price => async (dispatch, getState) => {
  dispatch(exchangeCurrencyRequested());
  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(exchangeCurrencyFailure(AppError.NOT_LOGGED_IN));
    return {
      error: AppError.NOT_LOGGED_IN
    };
  }

  const {
    error,
    data
  } = await ethereum.deposit({
    senderAddress: accounts[0],
    amount: price
  });

  if (error !== EipError.NO_ERROR) {
    dispatch(exchangeCurrencyFailure(error));
    return {
      error
    };
  }

  dispatch(exchangeCurrencySuccess());
  dispatch(updateWalletBalance());
  return {
    data: {
      id: data
    }
  };
};

const fetchIngameBalanceRequested = () => ({
  type: ActionType.FETCH_INGAME_BALANCE_REQUESTED,
  payload: undefined
});

const fetchIngameBalanceSuccess = data => ({
  type: ActionType.FETCH_INGAME_BALANCE_SUCCESS,
  payload: {
    data
  }
});

const fetchIngameBalanceFailure = error => ({
  type: ActionType.FETCH_INGAME_BALANCE_FAILURE,
  payload: {
    error
  }
});

export const fetchIngameBalance = () => async (dispatch, getState) => {
  dispatch(fetchIngameBalanceRequested());
  const result = await api.getIngameBalance({
    configVer: -1
  });

  if (result.status === 'error') {
    dispatch(fetchIngameBalanceFailure(result.error));
    return {
      error: result.error
    };
  } // if (result.body.config && result.body.userCap) {
  //   dispatch(
  //     pushCurrencyConfigToEntities({
  //       config: result.body.config,
  //       userCap: result.body.userCap,
  //     })
  //   );
  //   dispatch(saveCurrencyVerToEntities(result.body.config.version));
  // }


  const ingameBalance = getState().ingameBalance.data;
  const balanceWithoutUndefined = omitBy(result.body, isUndefined);
  dispatch(fetchIngameBalanceSuccess({ ...ingameBalance,
    ...balanceWithoutUndefined
  }));
  return {
    data: result.body
  };
}; // export const saveCurrencyVerToEntities = (currencyVer: number): Actions => ({
//   type: ActionType.SAVE_CURRENCY_VER,
//   payload: { currencyVer },
// });
// export const pushCurrencyConfigToEntities =
//   (
//     currencyConfig:
//       | {
//           userCap: IngameBalanceUserCap;
//           config: IngameBalanceConfig;
//         }
//       | undefined
//   ): AppThunk<Promise<void>> =>
//   async dispatch => {
//     dispatch({
//       type: ActionType.ADD_CURRENCY_CONFIG_ENTITIES,
//       payload: { currencyConfig },
//     });
//     return;
//   };

const claimReferralRewardRequested = () => ({
  type: ActionType.CLAIM_REFERRAL_REWARD_REQUESTED,
  payload: undefined
});

const claimReferralRewardSuccess = () => ({
  type: ActionType.CLAIM_REFERRAL_REWARD_SUCCESS,
  payload: undefined
});

const claimReferralRewardFailure = error => ({
  type: ActionType.CLAIM_REFERRAL_REWARD_FAILURE,
  payload: {
    error
  }
});

export const claimReferralRewardInfo = () => async dispatch => {
  dispatch(claimReferralRewardRequested());
  const result = await api.claimReferralReward({});

  if (result.status === 'error') {
    dispatch(claimReferralRewardFailure(result.error));
    return {
      error: result.error
    };
  }

  dispatch(claimReferralRewardSuccess());
  return {
    data: result.body
  };
};
export const getAvatarAllowedConfigs = () => async dispatch => {
  dispatch(fetchAvatarAllowedConfigRequested());
  const avatarAllowedResponse = await api.getAvatarAllowedConfigs({});

  if (avatarAllowedResponse.status === 'error') {
    dispatch(fetchAvatarAllowedConfigFailure(avatarAllowedResponse.error));
    return {
      error: avatarAllowedResponse.error
    };
  }

  const {
    avatars
  } = avatarAllowedResponse.body || {};
  if (avatars) dispatch(fetchAvatarAllowedConfigSuccess(avatars));
  return {
    data: avatars
  };
};

const updateUserProfileRequested = () => ({
  type: ActionType.UPDATE_USER_PROFILE_REQUESTED,
  payload: undefined
});

const updateUserProfileSuccess = user => ({
  type: ActionType.UPDATE_USER_PROFILE_SUCCESS,
  payload: {
    user
  }
});

const updateUserProfileFailure = error => ({
  type: ActionType.UPDATE_USER_PROFILE_FAILURE,
  payload: {
    error
  }
});

export const updateUserProfile = () => async dispatch => {
  dispatch(updateUserProfileRequested());
  const response = await api.getProfile({});

  if (response.status === 'error') {
    dispatch(updateUserProfileFailure(response.error));
    return {
      error: response.error
    };
  }

  dispatch(updateUserProfileSuccess(response.body));
  dispatch(getAvatarConfigs());
  dispatch(getAvatarAllowedConfigs());
  dispatch(fetchSelectedCosmetic());
  dispatch(fetchCosmeticConfigs());
  return {
    data: response.body
  };
};

const fetchCosmeticConfigRequested = () => ({
  type: ActionType.FETCH_COSMETIC_CONFIG_REQUESTED,
  payload: undefined
});

const fetchCosmeticConfigSuccess = ({
  cosmeticConfigs,
  cosmeticConfigVer,
  sellFee,
  maxSale,
  minSale,
  imgPathPattern
}) => ({
  type: ActionType.FETCH_COSMETIC_CONFIG_SUCCESS,
  payload: {
    cosmeticConfigVer,
    cosmeticConfigs,
    imgPathPattern,
    sellFee,
    minSale,
    maxSale
  }
});

const fetchCosmeticConfigFailure = error => ({
  type: ActionType.FETCH_COSMETIC_CONFIG_FAILURE,
  payload: {
    error
  }
});

export const fetchCosmeticConfigs = () => async (dispatch, getState) => {
  dispatch(fetchCosmeticConfigRequested());
  const response = await api.getCosmeticConfigs({
    v: getState().cosmeticItemsConfigs.cosmeticConfigVer
  });

  if (response.status === 'error') {
    dispatch(fetchCosmeticConfigFailure(response.error));
    return {
      error: response.error
    };
  }

  const {
    cosmeticConfigVer,
    cosmeticConfigs,
    imgPathPattern,
    sellFee,
    minSale,
    maxSale
  } = response.body;
  if (!!cosmeticConfigVer && !!cosmeticConfigs) dispatch(fetchCosmeticConfigSuccess({
    cosmeticConfigVer,
    imgPathPattern,
    sellFee,
    cosmeticConfigs,
    minSale,
    maxSale
  }));
  return {
    data: cosmeticConfigs
  };
};

const selectedCosmeticsRequested = () => ({
  type: ActionType.FETCH_SELECTED_COSMETIC_REQUESTED,
  payload: undefined
});

const selectedCosmeticsFailure = error => ({
  type: ActionType.FETCH_SELECTED_COSMETIC_FAILURE,
  payload: {
    error
  }
});

const selectedCosmeticsSuccess = data => ({
  type: ActionType.FETCH_SELECTED_COSMETIC_SUCCESS,
  payload: {
    data
  }
});

export const fetchSelectedCosmetic = () => async dispatch => {
  dispatch(selectedCosmeticsRequested());
  const response = await api.getSelectedCosmetics({});

  if (response.status === 'error') {
    dispatch(selectedCosmeticsFailure(response.error));
    return {
      error: response.error
    };
  }

  const {
    data
  } = response.body;
  dispatch(selectedCosmeticsSuccess(data));
  return {
    data
  };
};

const updateWalletWbnbBalanceAction = balance => ({
  type: ActionType.UPDATE_WALLET_WBNB_BALANCE,
  payload: {
    balance
  }
});

const updateWalletThcBalanceAction = balance => ({
  type: ActionType.UPDATE_WALLET_THC_BALANCE,
  payload: {
    balance
  }
});

const updateWalletThgBalanceAction = balance => ({
  type: ActionType.UPDATE_WALLET_THG_BALANCE,
  payload: {
    balance
  }
});

const updateWalletBusdBalanceAction = balance => ({
  type: ActionType.UPDATE_WALLET_BUSD_BALANCE,
  payload: {
    balance
  }
});

const updateWalletBusdtBalanceAction = balance => ({
  type: ActionType.UPDATE_WALLET_BUSDT_BALANCE,
  payload: {
    balance
  }
});

export const updateWalletBalance = () => async (dispatch, getState) => {
  var _getState$connectWall;

  const address = (_getState$connectWall = getState().connectWallet.accounts) === null || _getState$connectWall === void 0 ? void 0 : _getState$connectWall[0];

  if (!address) {
    return {
      nativeBalance: undefined,
      thetaBalance: undefined,
      busdBalance: undefined,
      busdtBalance: undefined,
      thgBalance: undefined
    };
  }

  const [nativeBalance, thetaBalance, busdBalance, thgBalance, busdtBalance] = await Promise.all([ethereum.getBalance(address, CurrencyType.WBNB).then(({
    error,
    balance
  }) => {
    if (error !== EipError.NO_ERROR || typeof balance === 'undefined') {
      return undefined;
    }

    dispatch(updateWalletWbnbBalanceAction(balance));
    return balance;
  }), ethereum.getThetaBalance(address).then(({
    error,
    balance
  }) => {
    if (error !== EipError.NO_ERROR || typeof balance === 'undefined') {
      return undefined;
    }

    dispatch(updateWalletThcBalanceAction(balance));
    return balance;
  }), ethereum.getBalance(address, CurrencyType.BUSD).then(({
    error,
    balance
  }) => {
    if (error !== EipError.NO_ERROR || typeof balance === 'undefined') {
      return undefined;
    }

    dispatch(updateWalletBusdBalanceAction(balance));
    return balance;
  }), ethereum.getBalance(address, CurrencyType.THG).then(({
    error,
    balance
  }) => {
    if (error !== EipError.NO_ERROR || typeof balance === 'undefined') {
      return undefined;
    }

    dispatch(updateWalletThgBalanceAction(balance));
    return balance;
  }), ethereum.getBalance(address, CurrencyType.BUSDT).then(({
    error,
    balance
  }) => {
    if (error !== EipError.NO_ERROR || typeof balance === 'undefined') {
      return undefined;
    }

    dispatch(updateWalletBusdtBalanceAction(balance));
    return balance;
  })]);
  return {
    nativeBalance,
    thetaBalance,
    busdBalance,
    thgBalance,
    busdtBalance
  };
};

const authenticateUserRequested = () => ({
  type: ActionType.AUTHENTICATE_USER_REQUESTED,
  payload: undefined
});

const authenticateUserSuccess = accessToken => ({
  type: ActionType.AUTHENTICATE_USER_SUCCESS,
  payload: {
    accessToken
  }
});

const authenticateUserFailure = error => ({
  type: ActionType.AUTHENTICATE_USER_FAILURE,
  payload: {
    error
  }
});

export const authenticateUser = nonce => async (dispatch, getState) => {
  let _nonce = nonce;
  dispatch(authenticateUserRequested());
  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(authenticateUserFailure(AppError.UNNKOWN));
    return undefined;
  }

  const account = accounts[0];

  if (!_nonce) {
    const nonceResponse = await api.getAuthNonce({
      Address: account
    });

    if (nonceResponse.status === 'error') {
      dispatch(authenticateUserFailure(nonceResponse.error));
      return undefined;
    }

    _nonce = nonceResponse.body.nonce.toString();
  }

  let signature;
  let signError;
  const provider = getPreferredWalletProviderName();

  if (provider === WalletProvider.SEQUENCE) {
    const result = await personalSignPolygon({
      address: account,
      data: _nonce
    });
    signature = result.signature;
    signError = result.error;
  } else {
    const result = await ethereum.personalSign({
      address: account,
      data: _nonce
    });
    signature = result.signature;
    signError = result.error;
  }

  if (signError !== EipError.NO_ERROR) {
    signError === EipError.USER_REJECTED_REQUEST ? dispatch(authenticateUserFailure(AppError.ABORTED_BY_USER)) : dispatch(authenticateUserFailure(AppError.UNNKOWN));
    return undefined;
  }

  const authResponse = await api.postAuthToken({
    address: account,
    signature: signature
  });

  if (authResponse.status === 'error') {
    dispatch(authenticateUserFailure(authResponse.error));
    return undefined;
  }

  const {
    accessToken,
    refreshToken
  } = authResponse.body;
  storeSession(accessToken, refreshToken);
  dispatch(authenticateUserSuccess(accessToken));
  const {
    data
  } = await dispatch(updateUserProfile());
  return data;
};
export const autoAuthenticateUser = () => async dispatch => {
  const session = getSession();

  if (!session) {
    return {};
  }

  const {
    data
  } = await dispatch(connectWallet());
  const {
    accessToken
  } = session;
  dispatch(authenticateUserSuccess(accessToken));
  const user = await dispatch(updateUserProfile());

  if (data && user && user.data) {
    return {
      data: {
        thcBalance: data.thcBalance,
        thgBalance: data.thgBalance,
        user: user.data,
        accounts: data.accounts
      }
    };
  }

  return {};
};

const linkWalletRequested = () => ({
  type: ActionType.LINK_WALLET_REQUESTED,
  payload: undefined
});

const linkWalletSuccess = () => ({
  type: ActionType.LINK_WALLET_SUCCESS,
  payload: undefined
});

const linkWalletFailure = error => ({
  type: ActionType.LINK_WALLET_FAILURE,
  payload: {
    error
  }
});

const linkWalletClearError = () => ({
  type: ActionType.LINK_WALLET_CLEAR_ERROR,
  payload: undefined
});

export const linkWallet = () => async (dispatch, getState) => {
  dispatch(linkWalletRequested());
  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(linkWalletFailure(AppError.UNNKOWN));
    return false;
  }

  const account = accounts[0];
  let signature;
  let signError;
  const provider = getPreferredWalletProviderName();

  if (provider === WalletProvider.SEQUENCE) {
    const result = await personalSignPolygon({
      address: account,
      data: account
    });
    signature = result.signature;
    signError = result.error;
  } else {
    const result = await ethereum.personalSign({
      address: account,
      data: account
    });
    signature = result.signature;
    signError = result.error;
  }

  if (signError !== EipError.NO_ERROR) {
    signError === EipError.USER_REJECTED_REQUEST ? dispatch(linkWalletFailure(AppError.ABORTED_BY_USER)) : dispatch(linkWalletFailure(AppError.UNNKOWN));
    return false;
  }

  const linkWalletResponse = await api.linkWallet({
    address: account,
    signature: signature,
    provider: getPreferredWalletProviderName()
  });

  if (linkWalletResponse.status === 'error') {
    dispatch(linkWalletFailure(linkWalletResponse.error));
    return false;
  }

  dispatch(linkWalletSuccess());
  api.putWalletConnect({
    walletType: provider ?? 'Unknown'
  }).catch(noop); // avoid logging error

  return true;
};

const connectWalletRequested = () => ({
  type: ActionType.CONNECT_WALLET_REQUESTED,
  payload: undefined
});

const connectWalletSuccess = accounts => ({
  type: ActionType.CONNECT_WALLET_SUCCESS,
  payload: {
    accounts
  }
});

const connectWalletFailure = error => ({
  type: ActionType.CONNECT_WALLET_FAILURE,
  payload: {
    error
  }
});

export const connectWallet = options => async dispatch => {
  const {
    ensureAddedThetaCoin
  } = options || {};
  dispatch(connectWalletRequested());
  const {
    error: metamaskError
  } = await ethereum.ensureProvider({
    ensureCorrectChainId: true,
    ensureAddedThetaCoin
  });

  if (metamaskError !== EipError.NO_ERROR) {
    dispatch(connectWalletFailure(metamaskError));
    return {};
  }

  const {
    accounts,
    error: accountError
  } = await ethereum.getAccounts();

  if (accountError !== EipError.NO_ERROR || typeof accounts === 'undefined') {
    dispatch(connectWalletFailure(accountError));
    return {};
  }

  dispatch(connectWalletSuccess(accounts));
  const data = await dispatch(updateWalletBalance());

  if (data.thetaBalance && data.thgBalance) {
    return {
      data: {
        thcBalance: data.thetaBalance,
        thgBalance: data.thgBalance,
        accounts
      }
    };
  }

  return {};
};
export const refreshWallet = () => async (dispatch, getState) => {
  const isSigned = !!getState().userProfile.user;

  if (!isSigned) {
    return;
  }

  const {
    error: metamaskError
  } = await ethereum.ensureProvider({
    ensureCorrectChainId: true
  });

  if (metamaskError !== EipError.NO_ERROR) {
    dispatch(connectWalletFailure(metamaskError));
    return;
  }

  const {
    accounts,
    error: accountError
  } = await ethereum.getAccounts();

  if (accountError !== EipError.NO_ERROR || typeof accounts === 'undefined') {
    dispatch(connectWalletFailure(accountError));
    return;
  }

  dispatch(connectWalletSuccess(accounts));
  dispatch(updateWalletBalance());
};
export const resetLinkWalletError = () => async dispatch => {
  dispatch(linkWalletClearError());
  return;
};
export const getWalletStatus = provider => async dispatch => {
  dispatch(connectWalletRequested());
  const {
    error: metamaskError
  } = await ethereum.ensureProvider({
    ensureCorrectChainId: true,
    preferred: provider
  });

  if (metamaskError !== EipError.NO_ERROR) {
    dispatch(connectWalletFailure(metamaskError));
    return WalletStatusResponse.UNKNOWN;
  }

  const {
    accounts,
    error: accountError
  } = await ethereum.getAccounts();

  if (accountError !== EipError.NO_ERROR || typeof accounts === 'undefined') {
    dispatch(connectWalletFailure(accountError));
    return WalletStatusResponse.UNKNOWN;
  }

  const walletStatusRes = await api.getWalletStatus({
    address: accounts[0]
  });
  dispatch(connectWalletSuccess(accounts));

  if (walletStatusRes.status === 'ok') {
    if (walletStatusRes.body) {
      return WalletStatusResponse.ACCOUNT_EXIST;
    }

    return WalletStatusResponse.ACCOUNT_NOT_EXIST;
  } else {
    return WalletStatusResponse.UNKNOWN;
  }
};

const logoutAction = () => ({
  type: ActionType.USER_SIGN_OUT,
  payload: undefined
});

export const logout = () => async dispatch => {
  clearSession();
  clearPreferredWalletProviderName();
  await destroyWalletClient();
  dispatch(logoutAction());
};

const createAccountRequested = () => ({
  type: ActionType.CREATE_ACCOUNT_REQUESTED,
  payload: undefined
});

const createAccountSuccess = (accessToken, refreshToken) => ({
  type: ActionType.CREATE_ACCOUNT_SUCCESS,
  payload: {
    accessToken,
    refreshToken
  }
});

const createAccountFailure = error => ({
  type: ActionType.CREATE_ACCOUNT_FAILURE,
  payload: {
    error
  }
});

export const resetErrorCreateAccount = () => async dispatch => {
  dispatch(createAccountFailure(AppError.NO_ERROR));
  dispatch(sendCodeToEmailFailure(AppError.NO_ERROR));
};
export const createAccount = (email, code, isCreateAccount) => async dispatch => {
  dispatch(createAccountRequested());

  if (isCreateAccount) {
    const response = await api.createAccount({
      email,
      code
    });

    if (response.status === 'error') {
      dispatch(createAccountFailure(response.error));
      return false;
    }

    dispatch(createAccountSuccess(response.body.accessToken, response.body.refreshToken));
    return true;
  }

  const response = await api.loginAccount({
    email,
    code
  });

  if (response.status === 'error') {
    dispatch(createAccountFailure(response.error));
    return false;
  }

  dispatch(createAccountSuccess(response.body.accessToken, response.body.refreshToken));
  return true;
};

const sendCodeToEmailRequested = email => ({
  type: ActionType.SEND_CODE_TO_EMAIL_REQUESTED,
  payload: {
    email
  }
});

const sendCodeToEmailSuccess = () => ({
  type: ActionType.SEND_CODE_TO_EMAIL_SUCCESS,
  payload: undefined
});

const sendCodeToEmailFailure = error => ({
  type: ActionType.SEND_CODE_TO_EMAIL_FAILURE,
  payload: {
    error
  }
});

export const sendCodeToEmail = (email, isCreateAccount) => async dispatch => {
  dispatch(sendCodeToEmailRequested(email));
  const response = await api.registerEmail({
    email,
    createAccount: isCreateAccount
  });

  if (response.status === 'error') {
    dispatch(sendCodeToEmailFailure(response.error));
    return false;
  }

  dispatch(sendCodeToEmailSuccess());
  return true;
};

const claimTokenRequested = () => ({
  type: ActionType.CLAIM_TOKEN_REQUESTED,
  payload: undefined
});

const claimTokenSuccess = id => ({
  type: ActionType.CLAIM_TOKEN_SUCCESS,
  payload: {
    id
  }
});

const claimTokenFailure = error => ({
  type: ActionType.CLAIM_TOKEN_FAILURE,
  payload: {
    error
  }
});

export const prepareClaimToken = params => async dispatch => {
  dispatch(claimTokenRequested());
  const {
    systemCurrency
  } = params;
  const result = await api.postClaimToken(parseServerCurrency(systemCurrency));

  if (result.status === 'error') {
    dispatch(claimTokenFailure(result.error));
    return {
      error: result.error,
      params
    };
  }

  return {
    data: result.body,
    params
  };
};
export const claimToken = args => async (dispatch, getState) => {
  const {
    data: preparedData,
    error: preparedError,
    params
  } = args;

  if (preparedError || preparedData === undefined) {
    const _error = preparedError || AppError.UNNKOWN;

    claimTokenFailure(_error);
    return {
      error: _error
    };
  }

  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(claimTokenFailure(AppError.NOT_LOGGED_IN));
    return {
      error: AppError.NOT_LOGGED_IN
    };
  }

  const {
    blockchainID,
    signature: signatureResponse,
    expiredAt,
    tokenAddress,
    transactionID
  } = preparedData;
  const {
    error: confirmError
  } = await ethereum.claimToken({
    id: blockchainID,
    amount: systemCurrencyToBlockchainNumber(params.systemCurrency),
    expiredAt,
    signature: signatureResponse,
    tokenAddress,
    accountAddress: accounts[0]
  });

  if (confirmError !== EipError.NO_ERROR) {
    dispatch(claimTokenFailure(confirmError));
    return {
      error: confirmError
    };
  }

  dispatch(claimTokenSuccess(transactionID)); // dispatch(saveCurrencyVerToEntities(-1)); // force api return new config

  dispatch(fetchIngameBalance());
  return {
    data: {
      id: transactionID
    },
    error: AppError.NO_ERROR
  };
};

const fetchReferralInfoRequested = () => ({
  type: ActionType.FETCH_REFERRAL_INFO_REQUESTED,
  payload: undefined
});

const fetchReferralInfoSuccess = data => ({
  type: ActionType.FETCH_REFERRAL_INFO_SUCCESS,
  payload: {
    data
  }
});

const fetchReferralInfoFailure = error => ({
  type: ActionType.FETCH_REFERRAL_INFO_FAILURE,
  payload: {
    error
  }
});

export const fetchReferralInfo = () => async dispatch => {
  dispatch(fetchReferralInfoRequested());
  const result = await api.getReferralInfo({});

  if (result.status === 'error') {
    dispatch(fetchReferralInfoFailure(result.error));
    return {
      error: result.error
    };
  }

  dispatch(fetchReferralInfoSuccess(result.body));
  return {
    data: result.body
  };
};

const fetchCosmeticAllInventoryRequested = () => ({
  type: ActionType.FETCH_COSMETIC_ALL_INVENTORY_REQUESTED,
  payload: undefined
});

const fetchCosmeticAllInventoryFailured = error => ({
  type: ActionType.FETCH_COSMETIC_ALL_INVENTORY_FAILURE,
  payload: {
    error
  }
});

const fetchCosmeticAllInventorySuccess = data => ({
  type: ActionType.FETCH_COSMETIC_ALL_INVENTORY_SUCCESS,
  payload: {
    data
  }
});

export const fetchCosmeticAllInventory = () => async dispatch => {
  dispatch(fetchCosmeticAllInventoryRequested());
  const searchParams = {
    findMode: 1
  };
  const response = await api.getCosmeticAllInventory(searchParams);

  if (response.status === 'error') {
    dispatch(fetchCosmeticAllInventoryFailured(response.error));
    return {
      error: response.error
    };
  }

  const {
    data
  } = response.body;
  dispatch(fetchCosmeticAllInventorySuccess(data));
  return {
    data
  };
};

const fetchUserThetanBoxListRequested = () => ({
  type: ActionType.FETCH_THETAN_BOX_LIST_REQUESTED,
  payload: undefined
});

const fetchUserThetanBoxListSuccess = data => ({
  type: ActionType.FETCH_THETAN_BOX_LIST_SUCCESS,
  payload: {
    data
  }
});

const fetchUserThetanBoxListFailure = error => ({
  type: ActionType.FETCH_THETAN_BOX_LIST_FAILURE,
  payload: {
    error
  }
});

export const fetchUserThetanBoxList = () => async dispatch => {
  dispatch(fetchUserThetanBoxListRequested());
  const response = await api.getUserThetanBoxList({});

  if (response.status === 'error') {
    dispatch(fetchUserThetanBoxListFailure(response.error));
    return undefined;
  }

  dispatch(fetchUserThetanBoxListSuccess(response.body));
  return response.body;
};

const fetchBalanceHistoryRequested = () => ({
  type: ActionType.FETCH_BALANCE_HISTORY_REQUESTED,
  payload: undefined
});

const fetchBalanceHistorySuccess = (data, totalItems) => ({
  type: ActionType.FETCH_BALANCE_HISTORY_SUCCESS,
  payload: {
    data,
    totalItems
  }
});

const fetchBalanceHistoryFailure = error => ({
  type: ActionType.FETCH_BALANCE_HISTORY_FAILURE,
  payload: {
    error
  }
});

export const fetchBalanceHistory = params => async dispatch => {
  dispatch(fetchBalanceHistoryRequested());
  const {
    page,
    time,
    type,
    size
  } = params;
  const response = await api.getBalanceHistory({
    from: (page - 1) * size,
    size: size,
    ti: time,
    ty: type
  });

  if (response.status === 'error') {
    dispatch(fetchBalanceHistoryFailure(response.error));
    return undefined;
  }

  const {
    body: {
      data,
      totalItems
    }
  } = response;
  dispatch(fetchBalanceHistorySuccess(data, totalItems));
  return data;
};

const fetchTransactionHistoryRequested = () => ({
  type: ActionType.FETCH_TRANSACTION_HISTORY_REQUESTED,
  payload: undefined
});

const fetchTransactionHistorySuccess = (data, totalItems) => ({
  type: ActionType.FETCH_TRANSACTION_HISTORY_SUCCESS,
  payload: {
    data,
    totalItems
  }
});

const fetchTransactionHistoryFailure = error => ({
  type: ActionType.FETCH_TRANSACTION_HISTORY_FAILURE,
  payload: {
    error
  }
});

export const fetchTransactionHistory = params => async dispatch => {
  dispatch(fetchTransactionHistoryRequested());
  const {
    page,
    time,
    type,
    size
  } = params;
  const response = await api.getTransactionHistory({
    from: (page - 1) * size,
    size: size,
    ti: time,
    ty: type
  });

  if (response.status === 'error') {
    dispatch(fetchTransactionHistoryFailure(response.error));
    return undefined;
  }

  const {
    body: {
      data,
      totalItems
    }
  } = response;
  dispatch(fetchTransactionHistorySuccess(data, totalItems));
  return data;
};

const fetchCosmeticHistoryRequested = () => ({
  type: ActionType.FETCH_COSMETIC_HISTORY_REQUESTED,
  payload: undefined
});

const fetchCosmeticHistorySuccess = (data, totalItems) => ({
  type: ActionType.FETCH_COSMETIC_HISTORY_SUCCESS,
  payload: {
    data,
    totalItems
  }
});

const fetchCosmeticHistoryFailure = error => ({
  type: ActionType.FETCH_COSMETIC_HISTORY_FAILURE,
  payload: {
    error
  }
});

export const fetchCosmeticHistory = params => async dispatch => {
  dispatch(fetchCosmeticHistoryRequested());
  const {
    page,
    time,
    type,
    size
  } = params;
  const response = await api.getCosmeticHistory({
    from: (page - 1) * size,
    size: size,
    ti: time,
    ty: type
  });

  if (response.status === 'error') {
    dispatch(fetchCosmeticHistoryFailure(response.error));
    return undefined;
  }

  const {
    body: {
      data,
      totalItems
    }
  } = response;
  dispatch(fetchCosmeticHistorySuccess(data, totalItems));
  return data;
};

const fetchHeroHistoryRequested = () => ({
  type: ActionType.FETCH_HERO_HISTORY_REQUESTED,
  payload: undefined
});

const fetchHeroHistorySuccess = (data, totalItems) => ({
  type: ActionType.FETCH_HERO_HISTORY_SUCCESS,
  payload: {
    data,
    totalItems
  }
});

const fetchHeroHistoryFailure = error => ({
  type: ActionType.FETCH_HERO_HISTORY_FAILURE,
  payload: {
    error
  }
});

export const fetchHeroHistory = params => async dispatch => {
  dispatch(fetchHeroHistoryRequested());
  const {
    page,
    time,
    type,
    size
  } = params;
  const response = await api.getHeroHistory({
    from: (page - 1) * size,
    size: size,
    ti: time,
    ty: type
  });

  if (response.status === 'error') {
    dispatch(fetchHeroHistoryFailure(response.error));
    return undefined;
  }

  const {
    body: {
      data,
      totalItems
    }
  } = response;
  dispatch(fetchHeroHistorySuccess(data, totalItems));
  return data;
};

const fetchRentalHistoryRequested = () => ({
  type: ActionType.FETCH_RENTAL_HISTORY_REQUESTED,
  payload: undefined
});

const fetchRentalHistorySuccess = (data, totalItems) => ({
  type: ActionType.FETCH_RENTAL_HISTORY_SUCCESS,
  payload: {
    data,
    totalItems
  }
});

const fetchRentalHistoryFailure = error => ({
  type: ActionType.FETCH_RENTAL_HISTORY_FAILURE,
  payload: {
    error
  }
});

export const fetchRentalHistory = params => async dispatch => {
  dispatch(fetchRentalHistoryRequested());
  const {
    page,
    time,
    type,
    size
  } = params;
  const response = await api.getRentalHistory({
    from: (page - 1) * size,
    size: size,
    ti: time,
    ty: type
  });

  if (response.status === 'error') {
    dispatch(fetchRentalHistoryFailure(response.error));
    return undefined;
  }

  const {
    body: {
      data,
      totalItems
    }
  } = response;
  dispatch(fetchRentalHistorySuccess(data, totalItems));
  return data;
};

const fetchVestingSafeHistoryRequested = () => ({
  type: ActionType.FETCH_VESTING_SAFE_HISTORY_REQUESTED,
  payload: undefined
});

const fetchVestingSafeHistorySuccess = (data, totalItems) => ({
  type: ActionType.FETCH_VESTING_SAFE_HISTORY_SUCCESS,
  payload: {
    data,
    totalItems
  }
});

const fetchVestingSafeHistoryFailure = error => ({
  type: ActionType.FETCH_VESTING_SAFE_HISTORY_FAILURE,
  payload: {
    error
  }
});

export const fetchVestingSafeHistory = param => async dispatch => {
  const {
    sources,
    size,
    page,
    time
  } = param;
  dispatch(fetchVestingSafeHistoryRequested());
  const response = await api.getVestingSafeHistory({
    from: (page - 1) * size,
    size: size,
    ti: time,
    sources
  });

  if (response.status === 'error') {
    dispatch(fetchVestingSafeHistoryFailure(response.error));
    return undefined;
  }

  const {
    body: {
      data,
      totalItems
    }
  } = response;
  dispatch(fetchVestingSafeHistorySuccess(data, totalItems));
  return data;
};

const fetchBattleRewardsRequested = () => ({
  type: ActionType.FETCH_BATTLE_REWARDS_REQUESTED,
  payload: undefined
});

const fetchBattleRewardsFailure = error => ({
  type: ActionType.FETCH_BATTLE_REWARDS_FAILURE,
  payload: {
    error
  }
});

const fetchBattleRewardsSuccess = (data, totalItems) => ({
  type: ActionType.FETCH_BATTLE_REWARDS_SUCCESS,
  payload: {
    data,
    totalItems
  }
});

export const fetchBattleRewards = (search, from, size) => async dispatch => {
  dispatch(fetchBattleRewardsRequested());
  const response = await api.getBattleRewards({
    s: search,
    from,
    size
  });

  if (response.status === 'error') {
    dispatch(fetchBattleRewardsFailure(response.error));
    return {
      error: response.error
    };
  }

  const {
    data,
    totalItems
  } = response.body;
  dispatch(fetchBattleRewardsSuccess(data, totalItems));
  return {
    data,
    totalItems
  };
};

const fetchCosmeticInventoryRequested = () => ({
  type: ActionType.FETCH_COSMETIC_INVENTORY_REQUESTED,
  payload: undefined
});

const fetchCosmeticInventoryFailured = error => ({
  type: ActionType.FETCH_COSMETIC_INVENTORY_FAILURE,
  payload: {
    error
  }
});

const fetchCosmeticInventorySuccess = (data, totalItems) => ({
  type: ActionType.FETCH_COSMETIC_INVENTORY_SUCCESS,
  payload: {
    data,
    totalItems
  }
});

export const fetchCosmeticInventory = ({
  from,
  size,
  types,
  sort
}) => async dispatch => {
  dispatch(fetchCosmeticInventoryRequested());
  let searchParams = {
    from,
    size,
    types
  };

  if (sort) {
    switch (sort) {
      case 'selected':
        {
          searchParams = { ...searchParams,
            findMode: 2
          };
          break;
        }

      default:
        {
          searchParams = { ...searchParams,
            status: sort.toString()
          };
          break;
        }
    }
  }

  const response = await api.getCosmeticInventory(searchParams);

  if (response.status === 'error') {
    dispatch(fetchCosmeticInventoryFailured(response.error));
    return {
      error: response.error
    };
  }

  const {
    data,
    totalItems
  } = response.body;
  dispatch(fetchCosmeticInventorySuccess(data, totalItems));
  return {
    data,
    totalItems
  };
};

const fetchInventoryResultRequested = () => ({
  type: ActionType.FETCH_INVENTORY_RESULT_REQUESTED,
  payload: undefined
});

const fetchInventoryResultSuccess = (searchParams, data, totalItems) => ({
  type: ActionType.FETCH_INVENTORY_RESULT_SUCCESS,
  payload: {
    searchParams,
    data,
    totalItems
  }
});

const fetchInventoryResultFailure = error => ({
  type: ActionType.FETCH_INVENTORY_RESULT_FAILURE,
  payload: {
    error
  }
});

export const fetchInventoryResult = searchParams => async dispatch => {
  dispatch(fetchInventoryResultRequested());
  const response = await api.searchInventory({ ...searchParams,
    skinVer: -1
  });

  if (response.status === 'error') {
    dispatch(fetchInventoryResultFailure(response.error));
    return undefined;
  }

  const {
    totalItems,
    heroes: items
  } = response.body; // if (!!skinConfigs && !!skinVer) {
  //   dispatch(pushSkinConfigToEntities(skinConfigs));
  //   dispatch(saveSkinVerToEntities(skinVer));
  // }

  dispatch(fetchInventoryResultSuccess(searchParams, items, totalItems));
  return items;
};

const fetchReferralFloatingInfoRequested = () => ({
  type: ActionType.FETCH_REFERRAL_FLOATING_INFO_REQUESTED,
  payload: undefined
});

const fetchReferralFloatingInfoSuccess = data => ({
  type: ActionType.FETCH_REFERRAL_FLOATING_INFO_SUCCESS,
  payload: {
    data
  }
});

const fetchReferralFloatingInfoFailure = error => ({
  type: ActionType.FETCH_REFERRAL_FLOATING_INFO_FAILURE,
  payload: {
    error
  }
});

export const fetchReferralFloatingInfo = () => async dispatch => {
  dispatch(fetchReferralFloatingInfoRequested());
  const result = await api.fetchReferralFloatingInfo({});

  if (result.status === 'error') {
    dispatch(fetchReferralFloatingInfoFailure(result.error));
    return {
      error: result.error
    };
  }

  dispatch(fetchReferralFloatingInfoSuccess(result.body));
  return {
    data: result.body
  };
};
export const searchInventory = searchParams => async dispatch => {
  return await dispatch(fetchInventoryResult(searchParams));
};

const fetchUserGiftBoxListRequested = () => ({
  type: ActionType.FETCH_GIFT_BOX_LIST_REQUESTED,
  payload: undefined
});

const fetchUserGiftBoxListSuccess = (data, isInstalledGame) => ({
  type: ActionType.FETCH_GIFT_BOX_LIST_SUCCESS,
  payload: {
    data,
    isInstalledGame
  }
});

const fetchUserGiftBoxListFailure = error => ({
  type: ActionType.FETCH_GIFT_BOX_LIST_FAILURE,
  payload: {
    error
  }
});

export const fetchUserGiftBoxList = () => async dispatch => {
  dispatch(fetchUserGiftBoxListRequested());
  const response = await api.getUserGiftBoxList({});

  if (response.status === 'error') {
    dispatch(fetchUserGiftBoxListFailure(response.error));
    return undefined;
  }

  const {
    data,
    isInstalledGame
  } = response.body;
  dispatch(fetchUserGiftBoxListSuccess(data, isInstalledGame));
  return {
    data,
    isInstalledGame
  };
};

const fetchBoxHistoryRequested = () => ({
  type: ActionType.FETCH_BOX_HISTORY_REQUESTED,
  payload: undefined
});

const fetchBoxHistorySuccess = (data, totalItems) => ({
  type: ActionType.FETCH_BOX_HISTORY_SUCCESS,
  payload: {
    data,
    totalItems
  }
});

const fetchBoxHistoryFailure = error => ({
  type: ActionType.FETCH_BOX_HISTORY_FAILURE,
  payload: {
    error
  }
});

export const fetchBoxHistory = params => async dispatch => {
  dispatch(fetchBoxHistoryRequested());
  const {
    page,
    time,
    type,
    size
  } = params;
  const response = await api.getBoxHistory({
    from: (page - 1) * size,
    size: size,
    ti: time,
    ty: type
  });

  if (response.status === 'error') {
    dispatch(fetchBoxHistoryFailure(response.error));
    return undefined;
  }

  const {
    body: {
      data,
      totalItems
    }
  } = response;
  dispatch(fetchBoxHistorySuccess(data, totalItems));
  return data;
};

const updateProfileRequested = () => ({
  type: ActionType.UPDATE_PROFILE_REQUESTED,
  payload: undefined
});

const updateProfileSuccess = () => ({
  type: ActionType.UPDATE_PROFILE_SUCCESS,
  payload: undefined
});

const updateProfileFailure = error => ({
  type: ActionType.UPDATE_PROFILE_FAILURE,
  payload: {
    error
  }
});

export const updateProfile = () => async dispatch => {
  dispatch(updateProfileRequested());
  const response = {
    status: 'sucess',
    error: AppError.NO_ERROR
  };

  if (response.status === 'error') {
    dispatch(updateProfileFailure(response.error));
    return;
  }

  dispatch(updateProfileSuccess());
  await dispatch(updateUserProfile());
  return;
};

const selectCosmeticsRequested = () => ({
  type: ActionType.FETCH_SELECT_COSMETIC_REQUESTED,
  payload: undefined
});

const selectCosmeticsFailure = error => ({
  type: ActionType.FETCH_SELECT_COSMETIC_FAILURE,
  payload: {
    error
  }
});

const selectCosmeticsSuccess = () => ({
  type: ActionType.FETCH_SELECT_COSMETIC_SUCCESS,
  payload: undefined
});

export const fetchSelectCosmetic = ({
  selected,
  type
}) => async dispatch => {
  dispatch(selectCosmeticsRequested());
  const response = await api.putSelectCosmetics({
    selected,
    type
  });

  if (response.status === 'error') {
    dispatch(selectCosmeticsFailure(response.error));
    return {
      error: response.error
    };
  }

  const {
    data
  } = response.body;
  dispatch(selectCosmeticsSuccess());
  return {
    data
  };
};